import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Avatar,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  MenuItem,
  Popover,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CheckCheck,
  MessageSquare,
  Mic,
  MoreVertical,
  Paperclip,
  RefreshCw,
  Search,
  Send,
  Smile,
  UserPlus,
  X,
} from 'lucide-react';
import { liveChatApi } from '../../../api/liveChat.api';
import { customersApi } from '../../../api/customers.api';
import { usersApi } from '../../../api';
import { uploadApi, validateMediaFile, FILE_ACCEPT_STRINGS } from '../../../api/upload.api';
import { useUIStore } from '../../../store';
import { ROLE_KEYS, getSessionRole } from '../../../utils/roleUtils';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

// WhatsApp Light Theme Colors
const WHATSAPP_GREEN = '#25d366';
const WHATSAPP_TEAL = '#128c7e';
const WHATSAPP_DARK_TEAL = '#075e54';
const WHATSAPP_LIGHT_GREEN = '#dcf8c6';
const WHATSAPP_CHAT_BG = '#e5ddd5';
const WHATSAPP_SIDEBAR_BG = '#ffffff';
const WHATSAPP_HOVER = '#f5f5f5';
const WHATSAPP_SELECTED = '#ebebeb';
const WHATSAPP_BORDER = '#e0e0e0';
const WHATSAPP_INPUT_BG = '#f0f2f5';
const EMOJI_SET = ['😀', '😁', '😂', '🤣', '😊', '😍', '😎', '🤔', '😅', '😭', '😡', '👍', '👎', '🙏', '👏', '🎉'];

const formatChatTime = (value) => {
  if (!value) return '';
  const date = dayjs(value);
  if (!date.isValid()) return '';
  const now = dayjs();
  if (date.isSame(now, 'day')) return date.format('HH:mm');
  if (date.isSame(now.subtract(1, 'day'), 'day')) return 'Yesterday';
  if (date.isAfter(now.subtract(7, 'day'))) return date.format('dddd');
  return date.format('DD/MM/YYYY');
};

const LiveChatPage = ({ session = {}, mode = '' }) => {
  const { showError, showSuccess } = useUIStore();
  const resolvedRole = mode || getSessionRole(session);
  const canAssign = resolvedRole === ROLE_KEYS.AGENT_ADMIN || resolvedRole === ROLE_KEYS.ADMIN || resolvedRole === ROLE_KEYS.CLIENT;
  const canUnassign = resolvedRole === ROLE_KEYS.AGENT_ADMIN || resolvedRole === ROLE_KEYS.ADMIN;
  const isAgent = resolvedRole === ROLE_KEYS.AGENT;
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [sessionDetail, setSessionDetail] = useState(null);
  const [customerDetail, setCustomerDetail] = useState(null);
  const [messages, setMessages] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [messageDraft, setMessageDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionCreating, setSessionCreating] = useState(false);
  const [newSessionOpen, setNewSessionOpen] = useState(false);
  const [newCustomerId, setNewCustomerId] = useState('');
  const [customersLoading, setCustomersLoading] = useState(false);
  const [customersList, setCustomersList] = useState([]);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [availableAgents, setAvailableAgents] = useState([]);
  const [activeAssignments, setActiveAssignments] = useState([]);
  const [assignedAgents, setAssignedAgents] = useState([]);
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [assignmentHistoryOpen, setAssignmentHistoryOpen] = useState(false);
  const [emojiAnchorEl, setEmojiAnchorEl] = useState(null);
  const [mediaAnchorEl, setMediaAnchorEl] = useState(null);
  const [mediaCategory, setMediaCategory] = useState('');
  const messagesEndRef = useRef(null);
  const mediaInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const messageInputRef = useRef(null);
  const sessionUid = session?.uid || session?.u_id || session?.uId || '';

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const getSessionLabel = (session) =>
    session?.cd_name ||
    session?.customer_name ||
    session?.customer?.name ||
    `Customer ${session?.cd_id || ''}`;
  const getSessionPhone = (session) => {
    const number =
      session?.cd_number ||
      session?.customer_phone ||
      session?.phone ||
      session?.customer?.phone ||
      '';
    const code = session?.phoneCode || session?.customer?.phoneCode || '';
    if (!number) return '';
    if (!code || String(number).startsWith('+')) return String(number);
    return `+${code} ${number}`;
  };
  const getCustomerPhone = (customer) => {
    const number = customer?.cd_number || customer?.phone || '';
    const code = customer?.phoneCode || customer?.phone_code || '';
    if (!number) return '';
    if (!code || String(number).startsWith('+')) return String(number);
    return `+${code} ${number}`;
  };

  const getCustomerLabel = (customer) => {
    const name = customer?.cd_name || customer?.name || '';
    const phone = getCustomerPhone(customer);
    if (name && phone) return `${name} - ${phone}`;
    return name || phone || `Customer ${customer?.cd_id || customer?.id || ''}`;
  };


  const getAssignedAgentLabel = (agent) => {
    const name =
      agent?.u_name ||
      agent?.name ||
      agent?.agent?.u_name ||
      agent?.agent?.name ||
      '';
    if (name) return name;
    const id =
      agent?.uid ||
      agent?.id ||
      agent?.agent?.uid ||
      agent?.agent?.id ||
      '';
    return `Agent ${id}`.trim();
  };

  const hydrateSessionsWithCustomers = async (list) => {
    const ids = Array.from(
      new Set(
        list
          .filter((session) => session?.cd_id && (!session.cd_name || !session.cd_number))
          .map((session) => session.cd_id)
      )
    );
    if (ids.length === 0) return list;

    const results = await Promise.allSettled(ids.map((id) => customersApi.getById(id)));
    const lookup = new Map();
    results.forEach((result) => {
      if (result.status !== 'fulfilled') return;
      const payload = result.value?.data?.data ?? result.value?.data ?? {};
      const customer = payload.customer || payload;
      if (customer?.cd_id) {
        lookup.set(customer.cd_id, customer);
      }
    });

    return list.map((session) => {
      const customer = lookup.get(session.cd_id);
      if (!customer) return session;
      return {
        ...session,
        cd_name: session.cd_name || customer.cd_name,
        cd_number: session.cd_number || customer.cd_number,
        phoneCode: session.phoneCode || customer.phoneCode,
      };
    });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const mergeSessions = (nextList, prevList) => {
    const prevMap = new Map(prevList.map((item) => [String(item.lcs_id), item]));
    const merged = nextList.map((item) => {
      const key = String(item.lcs_id);
      const prev = prevMap.get(key);
      prevMap.delete(key);
      return prev ? { ...prev, ...item } : item;
    });
    if (prevMap.size === 0) return merged;
    return [...merged, ...prevMap.values()];
  };

  const loadSessions = async ({ silent = false } = {}) => {
    if (!silent) {
      setSessionsLoading(false);
    }
    try {
      const response = await liveChatApi.getSessions();
      const payload = response?.data?.data ?? response?.data ?? {};
      const list = Array.isArray(payload.sessions) ? payload.sessions : [];
      const hydrated = await hydrateSessionsWithCustomers(list);
      setSessions((prev) => mergeSessions(hydrated, prev));
      if (!selectedSessionId && hydrated.length > 0) {
        setSelectedSessionId(hydrated[0].lcs_id);
      }
    } catch (error) {
      console.error('Failed to load sessions:', error);
      showError('Failed to load sessions');
    } finally {
      if (!silent) {
        setSessionsLoading(false);
      }
    }
  };

  const loadSessionDetail = async (lcsId) => {
    if (!lcsId) return;
    setDetailLoading(true);
    try {
      const response = await liveChatApi.getSessionById(lcsId);
      const payload = response?.data?.data ?? response?.data ?? {};
      setSessionDetail(payload.session || null);
      setCustomerDetail(payload.customer || null);
      setMessages(Array.isArray(payload.messages) ? payload.messages : []);
    } catch (error) {
      console.error('Failed to load session detail:', error);
      showError('Failed to load chat detail');
    } finally {
      setDetailLoading(false);
    }
  };

  const loadAssignedAgents = async (lcsId) => {
    if (!lcsId || !canAssign) return;
    try {
      const response = await liveChatApi.listSessionAgents(lcsId);
      const payload = response?.data?.data ?? response?.data ?? {};
      const list = payload.agents || payload.data || payload || [];
      setAssignedAgents(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error('Failed to load assigned agents:', error);
      setAssignedAgents([]);
    }
  };

  const loadAgents = async () => {
    if (!canAssign) return;
    setAgentsLoading(true);
    try {
      const response = await usersApi.getUsers();
      const payload = response?.data?.data ?? response?.data ?? [];
      const list = Array.isArray(payload) ? payload : [];
      const filtered = list.filter((item) => {
        const role = String(item.u_role || item.role || '').toLowerCase();
        const status = Number(item.u_status ?? item.status ?? 0);
        return role === 'agent' && status === 1;
      });
      const activeAssignedIds = new Set(
        activeAssignments
          .map((assignment) => assignment?.uid)
          .filter(Boolean)
          .map((id) => String(id))
      );
      const available = filtered.filter(
        (agent) => !activeAssignedIds.has(String(agent.uid || agent.id || ''))
      );
      setAvailableAgents(available);
    } catch (error) {
      console.error('Failed to load agents:', error);
      showError('Failed to load agents');
    } finally {
      setAgentsLoading(false);
    }
  };

  const loadActiveAssignments = async () => {
    if (!canAssign) return;
    try {
      const response = await liveChatApi.getActiveAssignments();
      const payload = response?.data?.data ?? response?.data ?? [];
      setActiveAssignments(Array.isArray(payload) ? payload : []);
    } catch (error) {
      console.error('Failed to load active assignments:', error);
      setActiveAssignments([]);
    }
  };

  const activeCustomerIds = useMemo(() => {
    const ids = sessions
      .filter((item) => String(item?.status || '').toLowerCase() === 'open')
      .map((item) => item?.cd_id || item?.cdId)
      .filter(Boolean)
      .map((id) => String(id));
    return new Set(ids);
  }, [sessions]);

  const loadCustomers = async () => {
    setCustomersLoading(true);
    try {
      const response = await customersApi.getAll({ page: 1, limit: 100 });
      const payload = response?.data || {};
      const list = Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload?.customers)
          ? payload.customers
          : Array.isArray(payload)
            ? payload
            : [];
      const activeOnly = list.filter((customer) => {
        const flag = customer?.cd_active ?? customer?.active ?? customer?.status;
        return String(flag) === '1' || flag === 1 || flag === true || String(flag).toLowerCase() === 'active';
      });
      const available = activeOnly.filter((customer) => {
        const id = customer?.cd_id || customer?.id;
        if (!id) return true;
        return !activeCustomerIds.has(String(id));
      });
      setCustomersList(available);
    } catch (error) {
      console.error('Failed to load customers:', error);
      showError('Failed to load customers');
      setCustomersList([]);
    } finally {
      setCustomersLoading(false);
    }
  };

  const getSessionAgentId = (sessionItem) =>
    sessionItem?.agent_id ||
    sessionItem?.assigned_agent_id ||
    sessionItem?.assigned_to ||
    sessionItem?.agent?.uid ||
    sessionItem?.assigned_agent?.uid ||
    sessionItem?.agent_uid ||
    '';

  const matchesAgent = (sessionItem) => {
    if (!sessionUid) return true;
    const assignedId = getSessionAgentId(sessionItem);
    if (!assignedId) return true;
    return String(assignedId) === String(sessionUid);
  };

  const refreshMessages = async () => {
    if (!selectedSessionId) return;
    try {
      const response = await liveChatApi.getMessages(selectedSessionId);
      const payload = response?.data?.data ?? response?.data ?? {};
      const list = Array.isArray(payload.messages) ? payload.messages : [];
      setMessages((prev) => {
        const existingIds = new Set(prev.map((msg) => String(msg.cm_id || msg.created_at || '')));
        const additions = list.filter((msg) => {
          const key = String(msg.cm_id || msg.created_at || '');
          return key && !existingIds.has(key);
        });
        if (additions.length === 0) return prev;
        return [...prev, ...additions];
      });
    } catch (error) {
      console.error('Failed to refresh messages:', error);
      showError('Failed to refresh messages');
    }
  };

  useEffect(() => {
    loadSessions();
  }, []);

  useEffect(() => {
    if (selectedSessionId) {
      loadSessionDetail(selectedSessionId);
      loadAssignedAgents(selectedSessionId);
    }
  }, [selectedSessionId]);

  useEffect(() => {
    if (!selectedSessionId) return;
    const intervalId = setInterval(() => {
      loadSessions({ silent: false });
      refreshMessages();
    }, 5000);
    return () => clearInterval(intervalId);
  }, [selectedSessionId]);

  useEffect(() => {
    if (canAssign) {
      loadActiveAssignments();
      loadAgents();
    }
  }, [canAssign, assignedAgents]);

  useEffect(() => {
    if (canAssign) {
      loadActiveAssignments();
    }
  }, [sessions, canAssign]);

  useEffect(() => {
    if (canAssign) {
      loadAgents();
    }
  }, [activeAssignments, canAssign]);

  useEffect(() => {
    if (newSessionOpen) {
      loadCustomers();
    }
  }, [newSessionOpen]);

  const filteredSessions = useMemo(() => {
    const query = search.trim().toLowerCase();
    const visibleSessions = isAgent ? sessions.filter(matchesAgent) : sessions;
    if (!query) return visibleSessions;
    return visibleSessions.filter((sessionItem) => {
      const label = getSessionLabel(sessionItem).toLowerCase();
      return label.includes(query) || String(sessionItem.lcs_id).includes(query);
    });
  }, [search, sessions, isAgent, sessionUid]);

  const openSessions = useMemo(
    () => filteredSessions.filter((sessionItem) => String(sessionItem?.status || '').toLowerCase() === 'open'),
    [filteredSessions]
  );

  const closedSessions = useMemo(
    () => filteredSessions.filter((sessionItem) => String(sessionItem?.status || '').toLowerCase() !== 'open'),
    [filteredSessions]
  );

  const assignmentHistory = useMemo(() => {
    const list = Array.isArray(messages) ? messages : [];
    return list.filter((message) => {
      if (message?.sender_type !== 'system') return false;
      const text = String(message?.message_text || '').toLowerCase();
      return text.includes('assigned by') || text.includes('unassigned by');
    });
  }, [messages]);

  const activeAssignedAgents = useMemo(
    () => assignedAgents.filter((agent) => Number(agent?.is_active ?? 1) === 1 || agent?.is_active === true),
    [assignedAgents]
  );

  const renderSessionList = (sessionList) => (
    <List disablePadding>
      {sessionList.map((sessionItem) => {
        const isActive = sessionItem.lcs_id === selectedSessionId;
        const isOpen = String(sessionItem.status || '').toLowerCase() === 'open';

        return (
          <ListItemButton
            key={sessionItem.lcs_id}
            selected={isActive}
            onClick={() => setSelectedSessionId(sessionItem.lcs_id)}
            sx={{
              px: 2,
              py: 1.5,
              bgcolor: isActive ? WHATSAPP_SELECTED : 'transparent',
              borderBottom: `1px solid ${WHATSAPP_BORDER}`,
              '&:hover': { bgcolor: WHATSAPP_HOVER },
              '&.Mui-selected': { bgcolor: WHATSAPP_SELECTED },
              '&.Mui-selected:hover': { bgcolor: WHATSAPP_SELECTED },
            }}
          >
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              badgeContent={
                isOpen ? (
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      bgcolor: WHATSAPP_GREEN,
                      borderRadius: '50%',
                      border: '2px solid #fff',
                    }}
                  />
                ) : null
              }
            >
              <Avatar
                sx={{
                  width: 50,
                  height: 50,
                  bgcolor: '#dfe5e7',
                  color: '#fff',
                  fontSize: '1.25rem',
                  fontWeight: 500,
                }}
              >
                {String(getSessionLabel(sessionItem) || 'C').charAt(0).toUpperCase()}
              </Avatar>
            </Badge>
            <Box sx={{ ml: 2, flex: 1, minWidth: 0 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography
                  sx={{
                    color: '#111b21',
                    fontWeight: 500,
                    fontSize: '1rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {getSessionLabel(sessionItem)}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: '#667781', fontSize: '0.75rem', ml: 1, flexShrink: 0 }}
                >
                  {sessionItem.updated_at ? formatChatTime(sessionItem.updated_at) : ''}
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.25 }}>
                <Typography
                  sx={{
                    color: '#667781',
                    fontSize: '0.875rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {`Session #${sessionItem.lcs_id}${getSessionPhone(sessionItem) ? ` - ${getSessionPhone(sessionItem)}` : ''}`}
                </Typography>
                <Chip
                  size="small"
                  label={sessionItem.status || 'open'}
                  sx={{
                    height: 20,
                    fontSize: '0.7rem',
                    bgcolor: isOpen ? 'rgba(37, 211, 102, 0.15)' : 'rgba(102, 119, 129, 0.15)',
                    color: isOpen ? WHATSAPP_TEAL : '#667781',
                    border: 'none',
                    '& .MuiChip-label': { px: 1 },
                  }}
                />
              </Stack>
            </Box>
          </ListItemButton>
        );
      })}
    </List>
  );

  const handleSend = async () => {
    const text = messageDraft.trim();
    if (!text || !selectedSessionId) return;
    setSending(true);
    try {
      const response = await liveChatApi.createMessage({
        lcs_id: selectedSessionId,
        message_text: text,
      });
      const payload = response?.data?.data ?? response?.data ?? null;
      if (payload) {
        setMessages((prev) => [...prev, payload]);
      }
      setMessageDraft('');
    } catch (error) {
      console.error('Failed to send message:', error);
      showError('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleMediaSelect = async (event) => {
    const file = event.target.files?.[0] || null;
    event.target.value = '';
    setMediaCategory('');
    if (!file) return;

    if (!selectedSessionId) {
      showError('Select a chat before sending media');
      return;
    }

    const validation = validateMediaFile(file, mediaCategory || null);
    if (!validation.valid) {
      showError(validation.error || 'Invalid media file');
      return;
    }

    setUploadingMedia(true);
    try {
      const response = await uploadApi.uploadMedia(file);
      const mediaId = response?.data?.id || response?.data?.data?.id || null;
      if (!mediaId) {
        showError('Upload failed: missing media id');
        return;
      }

      await liveChatApi.createMessage({
        lcs_id: selectedSessionId,
        media_id: mediaId,
        media_type: validation.category,
      });
      await refreshMessages();
    } catch (error) {
      console.error('Failed to upload media:', error);
      showError('Failed to upload media');
    } finally {
      setUploadingMedia(false);
    }
  };

  const handleMediaClick = (event) => {
    if (uploadingMedia) return;
    setMediaAnchorEl(event.currentTarget);
  };

  const handleMediaClose = () => {
    setMediaAnchorEl(null);
  };

  const handleMediaCategorySelect = (category) => {
    setMediaCategory(category);
    setMediaAnchorEl(null);
    requestAnimationFrame(() => {
      mediaInputRef.current?.click();
    });
  };

  const handleEmojiOpen = (event) => {
    setEmojiAnchorEl(event.currentTarget);
  };

  const handleEmojiClose = () => {
    setEmojiAnchorEl(null);
  };

  const handleEmojiSelect = (emoji) => {
    setMessageDraft((prev) => `${prev || ''}${emoji}`);
    messageInputRef.current?.focus();
  };

  const handleCloseSession = async () => {
    if (!selectedSessionId) return;
    try {
      const response = await liveChatApi.closeSession(selectedSessionId);
      const payload = response?.data?.data ?? response?.data ?? null;
      if (payload) {
        setSessionDetail(payload);
        setSessions((prev) => prev.map((session) =>
          session.lcs_id === selectedSessionId ? { ...session, status: payload.status } : session
        ));
      }
      showSuccess('Session closed');
    } catch (error) {
      console.error('Failed to close session:', error);
      showError('Failed to close session');
    }
  };

  const handleNewSession = async () => {
    if (!newCustomerId) return;
    if (activeCustomerIds.has(String(newCustomerId))) {
      showError('Customer already has an active chat');
      return;
    }
    setSessionCreating(true);
    try {
      const response = await liveChatApi.createSession({
        cd_id: Number(newCustomerId),
        channel: 'whatsapp',
      });
      const payload = response?.data?.data ?? response?.data ?? null;
      if (payload?.lcs_id) {
        setSelectedSessionId(payload.lcs_id);
      }
      await loadSessions();
      setNewCustomerId('');
      setNewSessionOpen(false);
      showSuccess('Session created');
    } catch (error) {
      console.error('Failed to create session:', error);
      showError('Failed to create session');
    } finally {
      setSessionCreating(false);
    }
  };

  const handleAssignAgent = async () => {
    if (!selectedSessionId || !selectedAgentId) return;
    setAssigning(true);
    try {
      await liveChatApi.assignAgent({
        lcs_id: selectedSessionId,
        agent_id: selectedAgentId,
      });
      await loadAssignedAgents(selectedSessionId);
      await loadActiveAssignments();
      setAssignDialogOpen(false);
      setSelectedAgentId('');
      showSuccess('Agent assigned');
    } catch (error) {
      console.error('Failed to assign agent:', error);
      showError('Failed to assign agent');
    } finally {
      setAssigning(false);
    }
  };

  const handleUnassignAgent = async (agentId) => {
    if (!selectedSessionId || !agentId) return;
    setAssigning(true);
    try {
      await liveChatApi.unassignAgent({
        lcs_id: selectedSessionId,
        agent_id: agentId,
      });
      await loadAssignedAgents(selectedSessionId);
      await loadActiveAssignments();
      showSuccess('Agent unassigned');
    } catch (error) {
      console.error('Failed to unassign agent:', error);
      showError('Failed to unassign agent');
    } finally {
      setAssigning(false);
    }
  };

  const isChatClosed = sessionDetail?.status === 'closed';
  const headerGradient = 'linear-gradient(110deg, #25d366, #1ebe5a)';
  const headerText = '#ffffff';
  const headerBorder = '#1ebe5a';

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 100px)', bgcolor: '#f0f2f5', borderRadius: 2, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', minHeight: 0 }}>
      {/* Left Sidebar - Sessions List */}
        <Box
          sx={{
            width: 400,
            display: 'flex',
            flexDirection: 'column',
            bgcolor: WHATSAPP_SIDEBAR_BG,
            borderRight: `1px solid ${WHATSAPP_BORDER}`,
            minHeight: 0,
          }}
        >
        {/* Sidebar Header */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            background: headerGradient,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            minHeight: 60,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Avatar
              sx={{
                width: 40,
                height: 40,
                bgcolor: headerText,
                color: headerBorder,
                fontSize: '1rem',
              }}
            >
              <MessageSquare size={20} />
            </Avatar>
            <Typography sx={{ color: headerText, fontWeight: 600, fontSize: '1.1rem' }}>
              Live Chat
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.5}>
            {!isAgent && (
              <Tooltip title="New chat">
                <IconButton size="small" sx={{ color: headerText }} onClick={() => setNewSessionOpen(true)}>
                  <UserPlus size={20} />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Refresh">
              <IconButton size="small" sx={{ color: headerText }} onClick={loadSessions}>
                <RefreshCw size={20} />
              </IconButton>
            </Tooltip>
            <IconButton size="small" sx={{ color: headerText }}>
              <MoreVertical size={20} />
            </IconButton>
          </Stack>
        </Box>

        {/* Search Box */}
        <Box sx={{ px: 1.5, py: 1, bgcolor: '#f6f6f6', borderBottom: `1px solid ${WHATSAPP_BORDER}` }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search or start new chat"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={18} color="#54656f" />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: '#fff',
                borderRadius: 2,
                '& fieldset': { border: 'none' },
                '& input': { color: '#111b21', py: 1, '&::placeholder': { color: '#667781', opacity: 1 } },
              },
            }}
          />
        </Box>

        {/* Sessions List */}
        <Box sx={{ flex: 1, overflowY: 'auto', bgcolor: '#fff', minHeight: 0 }}>
          {sessionsLoading ? (
            <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
              <CircularProgress size={28} sx={{ color: WHATSAPP_TEAL }} />
            </Stack>
          ) : filteredSessions.length === 0 ? (
            <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
              <MessageSquare size={48} color="#667781" />
              <Typography variant="body2" sx={{ color: '#667781', mt: 2 }}>
                No active sessions
              </Typography>
              {!isAgent && (
                <Button
                  variant="contained"
                  startIcon={<UserPlus size={16} />}
                  onClick={() => setNewSessionOpen(true)}
                  sx={{
                    mt: 2,
                    bgcolor: WHATSAPP_TEAL,
                    '&:hover': { bgcolor: WHATSAPP_DARK_TEAL },
                    textTransform: 'none',
                    borderRadius: 2,
                  }}
                >
                  Start new chat
                </Button>
              )}
            </Stack>
          ) : (
            <>
              {openSessions.length > 0 && (
                <Box>
                  <Box sx={{ px: 2, py: 1, bgcolor: '#f7f9fa', borderBottom: `1px solid ${WHATSAPP_BORDER}` }}>
                    <Typography variant="caption" sx={{ color: WHATSAPP_TEAL, fontWeight: 700, letterSpacing: 0.4 }}>
                      OPEN CHATS ({openSessions.length})
                    </Typography>
                  </Box>
                  {renderSessionList(openSessions)}
                </Box>
              )}
              {closedSessions.length > 0 && (
                <Box>
                  <Box sx={{ px: 2, py: 1, bgcolor: '#f7f9fa', borderBottom: `1px solid ${WHATSAPP_BORDER}` }}>
                    <Typography variant="caption" sx={{ color: '#667781', fontWeight: 700, letterSpacing: 0.4 }}>
                      CLOSED CHATS ({closedSessions.length})
                    </Typography>
                  </Box>
                  {renderSessionList(closedSessions)}
                </Box>
              )}
            <List disablePadding sx={{ display: 'none' }}>
              {filteredSessions.map((session) => {
                const isActive = session.lcs_id === selectedSessionId;
                const isOpen = session.status === 'open';
                return (
                  <ListItemButton
                    key={session.lcs_id}
                    selected={isActive}
                    onClick={() => setSelectedSessionId(session.lcs_id)}
                    sx={{
                      px: 2,
                      py: 1.5,
                      bgcolor: isActive ? WHATSAPP_SELECTED : 'transparent',
                      borderBottom: `1px solid ${WHATSAPP_BORDER}`,
                      '&:hover': { bgcolor: WHATSAPP_HOVER },
                      '&.Mui-selected': { bgcolor: WHATSAPP_SELECTED },
                      '&.Mui-selected:hover': { bgcolor: WHATSAPP_SELECTED },
                    }}
                  >
                    <Badge
                      overlap="circular"
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      badgeContent={
                        isOpen ? (
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              bgcolor: WHATSAPP_GREEN,
                              borderRadius: '50%',
                              border: '2px solid #fff',
                            }}
                          />
                        ) : null
                      }
                    >
                      <Avatar
                        sx={{
                          width: 50,
                          height: 50,
                          bgcolor: '#dfe5e7',
                          color: '#fff',
                          fontSize: '1.25rem',
                          fontWeight: 500,
                        }}
                      >
                        {String(getSessionLabel(session) || 'C').charAt(0).toUpperCase()}
                      </Avatar>
                    </Badge>
                    <Box sx={{ ml: 2, flex: 1, minWidth: 0 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography
                          sx={{
                            color: '#111b21',
                            fontWeight: 500,
                            fontSize: '1rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {getSessionLabel(session)}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: '#667781', fontSize: '0.75rem', ml: 1, flexShrink: 0 }}
                        >
                          {session.updated_at ? formatChatTime(session.updated_at) : ''}
                        </Typography>
                      </Stack>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.25 }}>
                        <Typography
                          sx={{
                            color: '#667781',
                            fontSize: '0.875rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {`Session #${session.lcs_id}${getSessionPhone(session) ? ` • ${getSessionPhone(session)}` : ''}`}
                        </Typography>
                        <Chip
                          size="small"
                          label={session.status || 'open'}
                          sx={{
                            height: 20,
                            fontSize: '0.7rem',
                            bgcolor: isOpen ? 'rgba(37, 211, 102, 0.15)' : 'rgba(102, 119, 129, 0.15)',
                            color: isOpen ? WHATSAPP_TEAL : '#667781',
                            border: 'none',
                            '& .MuiChip-label': { px: 1 },
                          }}
                        />
                      </Stack>
                    </Box>
                  </ListItemButton>
                );
              })}
            </List>
            </>
          )}
        </Box>
      </Box>

      {/* Right Side - Chat Window */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', bgcolor: WHATSAPP_CHAT_BG, minHeight: 0 }}>
        {!selectedSessionId ? (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: '#f0f2f5',
              borderBottom: `6px solid ${WHATSAPP_TEAL}`,
            }}
          >
            <Box
              sx={{
                width: 300,
                height: 300,
                mb: 4,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <MessageSquare size={150} color="#25d366" strokeWidth={1} />
            </Box>
            <Typography variant="h5" sx={{ color: '#41525d', fontWeight: 300, mb: 1 }}>
              Live Chat
            </Typography>
            <Typography sx={{ color: '#667781', textAlign: 'center', maxWidth: 450, lineHeight: 1.6 }}>
              Connect with your customers in real-time.
              <br />
              Select a conversation from the left or start a new chat.
            </Typography>
          </Box>
        ) : (
          <>
            {/* Chat Header */}
            <Box
              sx={{
                px: 2,
                py: 1,
                background: headerGradient,
                display: 'flex',
                alignItems: 'center',
                minHeight: 60,
              }}
            >
              <Badge
                overlap="circular"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                badgeContent={
                  !isChatClosed ? (
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        bgcolor: WHATSAPP_GREEN,
                        borderRadius: '50%',
                        border: '2px solid',
                        borderColor: headerBorder,
                      }}
                    />
                  ) : null
                }
              >
                <Avatar
                  sx={{
                    width: 40,
                    height: 40,
                    bgcolor: '#dfe5e7',
                    color: '#fff',
                    fontSize: '1rem',
                  }}
                >
                  {String(customerDetail?.cd_name || sessionDetail?.cd_id || 'C').charAt(0).toUpperCase()}
                </Avatar>
              </Badge>
              <Box sx={{ ml: 2, flex: 1 }}>
                <Typography sx={{ color: headerText, fontWeight: 500, fontSize: '1rem' }}>
                  {customerDetail?.cd_name || `Customer ${sessionDetail?.cd_id || ''}`}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="caption" sx={{ color: headerText, opacity: 0.8 }}>
                    {isChatClosed ? 'Chat closed' : 'Online'}
                  </Typography>
                  {sessionDetail?.channel && (
                    <Chip
                      size="small"
                      label={sessionDetail.channel}
                      sx={{
                        height: 18,
                        fontSize: '0.65rem',
                        bgcolor: 'rgba(255,255,255,0.2)',
                        color: headerText,
                        '& .MuiChip-label': { px: 0.75 },
                      }}
                    />
                  )}
                </Stack>
                {canAssign && activeAssignedAgents.length > 0 && (
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.25 }}>
                    <Typography variant="caption" sx={{ color: headerText, opacity: 0.75 }}>
                      Assigned:
                    </Typography>
                    <Stack direction="row" spacing={0.5}>
                      {activeAssignedAgents.slice(0, 2).map((agent) => (
                        <Chip
                          key={agent.uid || agent.id}
                          size="small"
                          label={getAssignedAgentLabel(agent)}
                          sx={{
                            height: 18,
                            fontSize: '0.65rem',
                            bgcolor: 'rgba(255,255,255,0.2)',
                            color: headerText,
                            '& .MuiChip-label': { px: 0.75 },
                          }}
                        />
                      ))}
                      {activeAssignedAgents.length > 2 && (
                        <Chip
                          size="small"
                          label={`+${activeAssignedAgents.length - 2}`}
                          sx={{
                            height: 18,
                            fontSize: '0.65rem',
                            bgcolor: 'rgba(255,255,255,0.2)',
                            color: headerText,
                            '& .MuiChip-label': { px: 0.75 },
                          }}
                        />
                      )}
                    </Stack>
                    {assignmentHistory.length > 0 && (
                      <Button
                        size="small"
                        onClick={() => setAssignmentHistoryOpen(true)}
                        sx={{
                          minWidth: 'auto',
                          px: 1,
                          height: 22,
                          fontSize: '0.65rem',
                          color: headerText,
                          textTransform: 'none',
                          border: '1px solid rgba(255,255,255,0.35)',
                          borderRadius: 2,
                        }}
                      >
                        View
                      </Button>
                    )}
                  </Stack>
                )}
              </Box>
              <Stack direction="row" spacing={0.5}>
                <Tooltip title="Refresh">
                  <IconButton size="small" sx={{ color: headerText }} onClick={refreshMessages}>
                    <RefreshCw size={20} />
                  </IconButton>
                </Tooltip>
                {canAssign && !isChatClosed && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setAssignDialogOpen(true)}
                    sx={{
                      ml: 1,
                      color: headerText,
                      borderColor: 'rgba(255,255,255,0.5)',
                      textTransform: 'none',
                      fontSize: '0.75rem',
                      '&:hover': { borderColor: headerText, bgcolor: 'rgba(255,255,255,0.1)' },
                    }}
                  >
                    Assign
                  </Button>
                )}
                <Tooltip title={isChatClosed ? 'Chat is closed' : 'Close chat'}>
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleCloseSession}
                      disabled={isChatClosed}
                      sx={{
                        ml: 1,
                        color: headerText,
                        borderColor: 'rgba(255,255,255,0.5)',
                        textTransform: 'none',
                        fontSize: '0.75rem',
                        '&:hover': { borderColor: headerText, bgcolor: 'rgba(255,255,255,0.1)' },
                        '&.Mui-disabled': { color: 'rgba(255,255,255,0.4)', borderColor: 'rgba(255,255,255,0.2)' },
                      }}
                    >
                      {isChatClosed ? 'Closed' : 'End Chat'}
                    </Button>
                  </span>
                </Tooltip>
              </Stack>
            </Box>

            {/* Messages Area */}
            <Box
              sx={{
                flex: 1,
                overflowY: 'auto',
                px: { xs: 2, md: 8 },
                py: 2,
                bgcolor: WHATSAPP_CHAT_BG,
                backgroundImage: `url("data:image/png;base64,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")`,
                backgroundRepeat: 'repeat',
                minHeight: 0,
              }}
            >
              {detailLoading ? (
                <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
                  <CircularProgress size={28} sx={{ color: WHATSAPP_TEAL }} />
                </Stack>
              ) : messages.length === 0 ? (
                <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      bgcolor: '#fff8e1',
                      borderRadius: 2,
                      px: 3,
                      py: 1.5,
                      color: '#856404',
                      fontSize: '0.85rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                    }}
                  >
                    Start a conversation with this customer
                  </Paper>
                </Stack>
              ) : (
                <Stack spacing={0.5}>
                  {messages.map((message, index) => {
                    const isIncoming = message.sender_type === 'customer';
                    const showDate = index === 0 || !dayjs(message.created_at).isSame(dayjs(messages[index - 1]?.created_at), 'day');

                    return (
                      <React.Fragment key={message.cm_id || `${message.created_at}-${index}`}>
                        {showDate && (
                          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                            <Paper
                              elevation={0}
                              sx={{
                                bgcolor: 'rgba(255,255,255,0.95)',
                                borderRadius: 2,
                                px: 2,
                                py: 0.5,
                                boxShadow: '0 1px 1px rgba(0,0,0,0.08)',
                              }}
                            >
                              <Typography variant="caption" sx={{ color: '#54656f', fontSize: '0.75rem' }}>
                                {dayjs(message.created_at).format('MMMM D, YYYY')}
                              </Typography>
                            </Paper>
                          </Box>
                        )}
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: isIncoming ? 'flex-start' : 'flex-end',
                            mb: 0.25,
                          }}
                        >
                          <Paper
                            elevation={0}
                            sx={{
                              maxWidth: '65%',
                              minWidth: 80,
                              px: 1.5,
                              py: 0.75,
                              borderRadius: 2,
                              bgcolor: isIncoming ? '#fff' : WHATSAPP_LIGHT_GREEN,
                              color: '#111b21',
                              position: 'relative',
                              borderTopLeftRadius: isIncoming ? 0 : 8,
                              borderTopRightRadius: isIncoming ? 8 : 0,
                              boxShadow: '0 1px 1px rgba(0,0,0,0.08)',
                              '&::before': isIncoming ? {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: -8,
                                width: 8,
                                height: 13,
                                bgcolor: '#fff',
                                clipPath: 'polygon(100% 0, 100% 100%, 0 0)',
                              } : {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                right: -8,
                                width: 8,
                                height: 13,
                                bgcolor: WHATSAPP_LIGHT_GREEN,
                                clipPath: 'polygon(0 0, 100% 0, 0 100%)',
                              },
                            }}
                          >
                            {isIncoming && (
                              <Typography
                                variant="caption"
                                sx={{
                                  display: 'block',
                                  color: WHATSAPP_TEAL,
                                  fontWeight: 600,
                                  mb: 0.25,
                                  fontSize: '0.75rem',
                                }}
                              >
                                {message.sender_name || 'Customer'}
                              </Typography>
                            )}
                            {message.message_text && (
                              <Typography
                                sx={{
                                  fontSize: '0.9rem',
                                  lineHeight: 1.4,
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word',
                                  color: '#111b21',
                                }}
                              >
                                {message.message_text}
                              </Typography>
                            )}
                            {message.media_url && (
                              <Typography variant="caption" sx={{ color: '#667781', display: 'block' }}>
                                📎 {message.media_url}
                              </Typography>
                            )}
                            <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={0.5} sx={{ mt: 0.25 }}>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: '#667781',
                                  fontSize: '0.65rem',
                                }}
                              >
                                {message.created_at ? dayjs(message.created_at).format('HH:mm') : ''}
                              </Typography>
                              {!isIncoming && (
                                <CheckCheck size={16} color="#53bdeb" />
                              )}
                            </Stack>
                          </Paper>
                        </Box>
                      </React.Fragment>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </Stack>
              )}
            </Box>

            {/* Input Area */}
            <Box
              sx={{
                px: 2,
                py: 1.5,
                bgcolor: WHATSAPP_INPUT_BG,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              {isChatClosed ? (
                <Typography sx={{ color: '#667781', fontSize: '0.875rem', flex: 1, textAlign: 'center' }}>
                  This chat has been closed. Start a new session to continue messaging.
                </Typography>
              ) : (
                <>
                  <Tooltip title="Emoji">
                    <IconButton size="small" sx={{ color: '#54656f' }} onClick={handleEmojiOpen}>
                      <Smile size={24} />
                    </IconButton>
                  </Tooltip>
                  <Popover
                    open={Boolean(emojiAnchorEl)}
                    anchorEl={emojiAnchorEl}
                    onClose={handleEmojiClose}
                    anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    PaperProps={{ sx: { p: 1, borderRadius: 2, maxWidth: 260 } }}
                  >
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 240 }}>
                      {EMOJI_SET.map((emoji) => (
                        <IconButton
                          key={emoji}
                          size="small"
                          onClick={() => handleEmojiSelect(emoji)}
                          sx={{ fontSize: '1.1rem', width: 32, height: 32 }}
                        >
                          {emoji}
                        </IconButton>
                      ))}
                    </Box>
                  </Popover>
                  <Tooltip title="Attach">
                    <IconButton
                      size="small"
                      sx={{ color: '#54656f' }}
                      onClick={handleMediaClick}
                      disabled={uploadingMedia}
                    >
                      {uploadingMedia ? <CircularProgress size={20} sx={{ color: '#54656f' }} /> : <Paperclip size={24} />}
                    </IconButton>
                  </Tooltip>
                  <Popover
                    open={Boolean(mediaAnchorEl)}
                    anchorEl={mediaAnchorEl}
                    onClose={handleMediaClose}
                    anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    PaperProps={{ sx: { p: 1, borderRadius: 2, minWidth: 180 } }}
                  >
                    <Typography variant="subtitle2" sx={{ px: 1, pb: 0.5 }}>
                      Choose media type
                    </Typography>
                    <List dense sx={{ py: 0 }}>
                      {['image', 'video', 'audio', 'document'].map((category) => (
                        <ListItem key={category} disableGutters>
                          <ListItemButton onClick={() => handleMediaCategorySelect(category)}>
                            <Typography sx={{ textTransform: 'capitalize' }}>
                              {category}
                            </Typography>
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  </Popover>
                  <input
                    ref={mediaInputRef}
                    type="file"
                    accept={mediaCategory ? FILE_ACCEPT_STRINGS[mediaCategory] : FILE_ACCEPT_STRINGS.all}
                    style={{ display: 'none' }}
                    onChange={handleMediaSelect}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Type a message"
                    value={messageDraft}
                    onChange={(event) => setMessageDraft(event.target.value)}
                    inputRef={messageInputRef}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' && !event.shiftKey) {
                        event.preventDefault();
                        handleSend();
                      }
                    }}
                    multiline
                    maxRows={4}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: '#fff',
                        borderRadius: 2,
                        '& fieldset': { border: 'none' },
                        '& textarea': { color: '#111b21', '&::placeholder': { color: '#667781', opacity: 1 } },
                      },
                    }}
                  />
                    {messageDraft.trim() ? (
                      <Tooltip title="Send">
                        <IconButton
                          onClick={handleSend}
                          disabled={sending}
                          sx={{
                            color: '#fff',
                            bgcolor: WHATSAPP_TEAL,
                            '&:hover': { bgcolor: WHATSAPP_DARK_TEAL },
                            '&.Mui-disabled': { bgcolor: '#ccc', color: '#fff' },
                          }}
                        >
                          {sending ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : <Send size={20} />}
                        </IconButton>
                      </Tooltip>
                    ) : null}
                </>
              )}
            </Box>
          </>
        )}
      </Box>

      {!isAgent && (
        <Dialog
          open={newSessionOpen}
          onClose={() => setNewSessionOpen(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
            },
          }}
        >
          <DialogTitle sx={{ background: headerGradient, color: headerText }}>
            Start New Chat
          </DialogTitle>
          <DialogContent sx={{ pt: 3, pb: 2 }}>
            <TextField
              select
              label="Customer"
              fullWidth
              value={newCustomerId}
              onChange={(event) => setNewCustomerId(event.target.value)}
              sx={{ mt: 1 }}
            >
            {customersLoading && (
              <MenuItem value="" disabled>
                Loading customers...
              </MenuItem>
            )}
            {!customersLoading && customersList.length === 0 && (
              <MenuItem value="" disabled>
                No customers found
              </MenuItem>
            )}
              {customersList.map((customer) => (
                <MenuItem key={customer.cd_id || customer.id} value={customer.cd_id || customer.id}>
                  {getCustomerLabel(customer)}
                </MenuItem>
              ))}
            </TextField>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={() => setNewSessionOpen(false)}
              startIcon={<X size={16} />}
              sx={{ color: '#667781', textTransform: 'none' }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleNewSession}
              variant="contained"
              disabled={sessionCreating || !newCustomerId}
              sx={{
                bgcolor: WHATSAPP_TEAL,
                '&:hover': { bgcolor: WHATSAPP_DARK_TEAL },
                textTransform: 'none',
              }}
            >
              {sessionCreating ? 'Creating...' : 'Start Chat'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {canAssign && (
        <Dialog
          open={assignDialogOpen}
          onClose={() => setAssignDialogOpen(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
            },
          }}
        >
          <DialogTitle sx={{ background: headerGradient, color: headerText, mb: 2 }}>
            Assign Chat to Agent
          </DialogTitle>
          <DialogContent sx={{ pt: 4, pb: 2 }}>
            <Stack spacing={2}>
              <TextField
                select
                label="Agent"
                fullWidth
                value={selectedAgentId}
                onChange={(event) => setSelectedAgentId(event.target.value)}
                sx={{ mt: 1 }}
              >
                {agentsLoading && (
                  <MenuItem value="" disabled>
                    Loading agents...
                  </MenuItem>
                )}
                {!agentsLoading && availableAgents.length === 0 && (
                  <MenuItem value="" disabled>
                    No agents available
                  </MenuItem>
                )}
                {availableAgents.map((agent) => (
                  <MenuItem key={agent.uid} value={agent.uid}>
                    {agent.u_name || agent.name || `Agent ${agent.uid}`}
                  </MenuItem>
                ))}
              </TextField>
              {activeAssignedAgents.length > 0 && (
                <Typography variant="caption" sx={{ color: '#667781' }}>
                  This chat is already assigned to {activeAssignedAgents.map(getAssignedAgentLabel).join(', ')}.
                </Typography>
              )}
              {canAssign && activeAssignedAgents.length > 0 && (
                <Stack spacing={1}>
                  <Typography variant="subtitle2">Assigned agents</Typography>
                  {activeAssignedAgents.map((agent) => (
                    <Stack key={agent.uid || agent.id} direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2">
                        {getAssignedAgentLabel(agent)}
                      </Typography>
                      {canUnassign && (
                        <Button
                          size="small"
                          onClick={() => handleUnassignAgent(agent.uid || agent.id)}
                          disabled={assigning}
                          sx={{ textTransform: 'none' }}
                        >
                          Unassign
                        </Button>
                      )}
                    </Stack>
                  ))}
                </Stack>
              )}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, pt: 1, gap: 1, justifyContent: 'flex-end' }}>
            <Button
              onClick={() => setAssignDialogOpen(false)}
              startIcon={<X size={16} />}
              sx={{ color: '#667781', textTransform: 'none' }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAssignAgent}
              variant="contained"
              disabled={assigning || !selectedAgentId}
              sx={{
                bgcolor: WHATSAPP_TEAL,
                '&:hover': { bgcolor: WHATSAPP_DARK_TEAL },
                textTransform: 'none',
              }}
            >
              {assigning ? 'Assigning...' : 'Assign'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <Dialog
        open={assignmentHistoryOpen}
        onClose={() => setAssignmentHistoryOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle sx={{ background: headerGradient, color: headerText }}>
          Assignment History
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          {assignmentHistory.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No assignment history available.
            </Typography>
          ) : (
            <List dense sx={{ py: 0 }}>
              {assignmentHistory.map((message) => (
                <ListItem key={message.cm_id || `${message.created_at}-${message.message_text}`} disableGutters sx={{ py: 0.75 }}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {message.message_text || 'Assignment update'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {message.created_at ? dayjs(message.created_at).format('MMM D, YYYY h:mm A') : ''}
                    </Typography>
                  </Box>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setAssignmentHistoryOpen(false)}
            sx={{ color: '#667781', textTransform: 'none' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LiveChatPage;
